import { useEffect, useId, useRef, useState } from 'react';
import { useLazyLookupSkuQuery } from '@/entities/shop';
import type { Product } from '@/entities/label';
import { errorText } from '@/shared/api';
import { filledFields } from '../lib/fill';

interface Props {
  tab: number;                                   // the label whose tab is open
  product: Product;                              // its data
  onSku: (sku: string) => void;                  // typing in the SKU box
  readProduct: (idx: number) => Product;         // a label's data as it is now
  fill: (idx: number, patch: Partial<Product>) => void;
}

// The SKU box and "↓ Fill from Website": looks the SKU up on the website and fills name, description, price, compare-at and the
// QR link for the label that was open when the button was pressed. The message under the box: green done, grey working,
// a yellow note box when it filled but has something to say, red errors.
export function SkuField({ tab, product, onSku, readProduct, fill }: Props) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<{ msg: string; cls: '' | 'ok' | 'busy' | 'warn' | 'err' }>({ msg: '', cls: '' });
  const [busy, setBusy] = useState(false);
  const [lookup] = useLazyLookupSkuQuery();
  const openTab = useRef(tab);
  useEffect(() => { openTab.current = tab; }, [tab]);

  const run = async () => {
    if (busy) return;
    const idx = tab;
    const sku = readProduct(idx).sku.trim();
    if (!sku) {
      setStatus({ msg: 'Enter a SKU first.', cls: 'err' });
      input.current?.focus();
      return;
    }
    setBusy(true);
    setStatus({ msg: 'Looking up ' + sku + '…', cls: 'busy' });
    try {
      const data = await lookup(sku).unwrap();
      fill(idx, filledFields(data, sku, readProduct(idx)));
      const where = openTab.current === idx ? '' : ' (Label ' + (idx + 1) + ')';
      const notes = (data.warnings || []).length ? ' Note: ' + data.warnings.join('; ') + '.' : '';
      setStatus({ msg: '✓ Filled from website' + where + '.' + notes, cls: notes ? 'warn' : 'ok' });
    } catch (e) {
      setStatus({ msg: errorText(e, 'Lookup failed.'), cls: 'err' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="field">
      <label htmlFor={id}>SKU</label>
      <div className="sku-row">
        <input id={id} ref={input} type="text" placeholder="ABC-001" value={product.sku}
          onChange={(e) => onSku(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); run(); } }} />
        <button type="button" className="btn amber" disabled={busy} onClick={run}
          title="Fill name, description, prices and QR link from unclewiener.com">↓ Fill from Website</button>
      </div>
      <div className={`sku-status ${status.cls}`} role="status">{status.msg}</div>
    </div>
  );
}
